import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { environment } from '../../environments/environment';
import {
  GetCameraSnapshotsRequest,
  HasItems,
  LocationSnapshotResponse,
  SnapshotRequestResponse,
  SnapshotUpdateRequest,
  UpdateSnapshotSchedulesRequest,
} from '../types';

@Injectable({
  providedIn: 'root',
})
export class LocationSnapshotsService {
  private httpClient = inject(HttpClient);
  readonly apiHost = `${environment.apiBaseUrl}`;

  getLocationSnapshot(url: string) {
    return this.httpClient.get(url, {
      responseType: 'blob',
    });
  }

  getBranchLocationSnapshots(branchId: number, locationIds: number[] | null, snapshotDate: Date) {
    let params = new HttpParams({
      fromObject: {
        ...(snapshotDate && {
          snapshot_date: DateTime.fromJSDate(snapshotDate).toISODate()!,
        }),
      },
    });

    if (locationIds?.length) {
      locationIds.forEach((id) => {
        params = params.append('location_ids', id.toString());
      });
    }

    return this.httpClient
      .get<HasItems<LocationSnapshotResponse>>(`${this.apiHost}/Branches/${branchId}/LocationSnapshots`, { params })
      .pipe(map((res) => res.items));
  }

  getLocationSnapshots(locationId: number, snapshotDate: Date) {
    const params = new HttpParams({
      fromObject: {
        ...(snapshotDate && {
          snapshot_date: DateTime.fromJSDate(snapshotDate).toISODate()!,
        }),
      },
    });

    return this.httpClient
      .get<HasItems<LocationSnapshotResponse>>(`${this.apiHost}/Locations/${locationId}/LocationSnapshots`, { params })
      .pipe(map((res) => res.items));
  }

  updateSnapshotRequest(locationId: number, requestId: string, data: SnapshotUpdateRequest) {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/LocationSnapshotRequests/${requestId}`;
    return this.httpClient.patch(requestUrl, data);
  }

  updateLocationSchedules(locationId: number, request: UpdateSnapshotSchedulesRequest) {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/SnapshotSchedules`;
    return this.httpClient.put(requestUrl, request);
  }

  createLocationSnapshotRequest(locationId: number, zipBlob: Blob, requestDateTime: string) {
    const formData = new FormData();
    formData.append('file', zipBlob, 'snapshots.zip');
    const params = new HttpParams({ fromObject: { request_date_time: requestDateTime } });
    return this.httpClient.post<SnapshotRequestResponse>(`${this.apiHost}/Locations/${locationId}/LocationSnapshotRequests`, formData, {
      params,
    });
  }

  getCameraSnapshots(locationId: number, request: GetCameraSnapshotsRequest) {
    return this.httpClient.post(`${this.apiHost}/Locations/${locationId}/CameraSnapshots`, request, { responseType: 'blob' });
  }
}
